import { useState } from "react";
import { PageHero } from "@/components/Section";
import useReveal from "@/hooks/useReveal";
import { X } from "lucide-react";

const CDN =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_strange-cartwright-8/artifacts";

const photos = [
  {
    src: `${CDN}/bf7g84zx_IMG_5577.jpg`,
    alt: "Sunday morning worship at New Philadelphia",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    src: `${CDN}/gz83939m_709226385_122123167863216091_9060692169340108500_n.jpg`,
    alt: "Inside the sanctuary",
  },
  {
    src: `${CDN}/vxoldu53_616955902_122105707497216091_3578243584108528149_n.jpg`,
    alt: "The church building on Mendenhall",
  },
  {
    src: `${CDN}/5tasfvv2_IMG_5564.jpg`,
    alt: "Congregation gathered in worship",
    span: "md:row-span-2",
  },
  {
    src: `${CDN}/ers62mij_IMG_5591.jpg`,
    alt: "Faces of the New Philadelphia family",
  },
  {
    src: `${CDN}/vp94qujn_IMG_5420.jpeg`,
    alt: "A moment of worship",
    span: "md:col-span-2",
  },
  {
    src: `${CDN}/9mirgyyi_616808285_122102915913216091_622664071237135384_n.jpg`,
    alt: "Invitation to worship with us",
  },
];

export default function Gallery() {
  useReveal();
  const [lightbox, setLightbox] = useState(null);

  return (
    <div data-testid="gallery-page">
      <PageHero
        eyebrow="Life at New Philadelphia"
        title="Come and see."
        subtitle="Moments of worship, fellowship, and family from the New Philadelphia congregation. There is a seat waiting for you."
        image={`${CDN}/bf7g84zx_IMG_5577.jpg`}
      />

      <section className="bg-white py-20 md:py-28">
        <div className="container-npbc">
          <div className="grid auto-rows-[220px] grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
            {photos.map((p, i) => (
              <button
                key={p.src}
                type="button"
                onClick={() => setLightbox(p)}
                data-reveal
                data-testid={`gallery-photo-${i}`}
                style={{ transitionDelay: `${(i % 6) * 60}ms` }}
                className={`group relative overflow-hidden rounded-2xl bg-npbc-black/5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_20px_60px_-30px_rgba(0,0,0,0.2)] hover:shadow-[0_1px_2px_rgba(0,0,0,0.05),0_36px_80px_-30px_rgba(0,0,0,0.35)] transition-all duration-500 ${p.span || ""}`}
              >
                <img
                  src={p.src}
                  alt={p.alt}
                  className="h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
                  loading="lazy"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 p-4 text-left opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                  <p className="text-white text-sm font-medium drop-shadow">{p.alt}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-npbc-black text-white py-20">
        <div className="container-npbc text-center max-w-2xl mx-auto" data-reveal>
          <div className="eyebrow !text-npbc-gold justify-center flex">Come Worship With Us</div>
          <h2 className="mt-3 heading-serif text-4xl md:text-5xl">These photos are pretty. Being here is better.</h2>
          <p className="mt-4 text-white/75">Sunday worship at 11:00 AM. Bible Study every Wednesday at 6:00 PM. All are welcome — always.</p>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 backdrop-blur"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            aria-label="Close"
            data-testid="gallery-lightbox-close"
            className="absolute right-5 top-5 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            className="max-h-[90vh] max-w-[92vw] rounded-xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
